import React, { useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const iconPulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

// Styled components
const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  background-color: ${props => props.theme.colors.background};
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.small};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  opacity: 0;
  animation: ${fadeInUp} 0.5s ease forwards;
  animation-delay: ${props => props.delay || '0s'};
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

const FeatureIconContainer = styled.div`
  width: 80px;
  height: 80px;
  background-color: ${props => props.backgroundColor || props.theme.colors.primaryLight};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  transition: transform 0.3s ease;
  
  &:hover {
    animation: ${iconPulse} 1s ease infinite;
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text};
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const FeatureLink = styled.a`
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  text-decoration: none;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
`;

// Feature background colors
const featureColors = [
  'rgba(54, 162, 235, 0.2)',  // Blue
  'rgba(255, 99, 132, 0.2)',   // Red
  'rgba(75, 192, 192, 0.2)',   // Teal
  'rgba(255, 159, 64, 0.2)',   // Orange
  'rgba(153, 102, 255, 0.2)',  // Purple
  'rgba(255, 205, 86, 0.2)',   // Yellow
];

// Features component
const Features = () => {
  const { translate } = useLanguage();
  const featuresRef = useRef(null);
  
  // Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // When the section is visible, make sure animations can play
            const featureCards = document.querySelectorAll('.feature-card');
            featureCards.forEach((card, index) => {
              card.style.animationDelay = `${0.1 * index}s`;
              card.style.animationPlayState = 'running';
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    if (featuresRef.current) {
      observer.observe(featuresRef.current);
    }
    
    return () => {
      if (featuresRef.current) {
        observer.unobserve(featuresRef.current);
      }
    };
  }, []);
  
  // Feature data
  const features = [
    {
      icon: '🌦️',
      title: translate('features.realTime.title', 'Real-Time Weather Reports'),
      description: translate('features.realTime.description', 'Share and view current weather conditions in your area and around the world through community reports.'),
      link: '#manual',
      linkText: translate('features.realTime.link', 'Learn how it works'),
      color: featureColors[0],
    },
    {
      icon: '🗺️',
      title: translate('features.interactiveMap.title', 'Interactive Weather Map'),
      description: translate('features.interactiveMap.description', 'Explore an interactive map that displays weather reports as emoji icons, with size reflecting community validation.'),
      link: '#data',
      linkText: translate('features.interactiveMap.link', 'View the map'),
      color: featureColors[1],
    },
    {
      icon: '👥',
      title: translate('features.community.title', 'Community Validation'),
      description: translate('features.community.description', 'Vote on the accuracy of reports to help the community identify the most reliable weather information.'),
      link: '#community',
      linkText: translate('features.community.link', 'Join the community'),
      color: featureColors[2],
    },
    {
      icon: '📱',
      title: translate('features.profiles.title', 'Multiple User Profiles'),
      description: translate('features.profiles.description', 'Create and switch between different profiles with custom settings for various reporting needs.'),
      link: '#manual',
      linkText: translate('features.profiles.link', 'Customize your experience'),
      color: featureColors[3],
    },
    {
      icon: '📍',
      title: translate('features.nearMe.title', '"Ask Near Me" Feature'),
      description: translate('features.nearMe.description', 'Quickly find weather reports in your vicinity with a single tap, perfect for on-the-go decision making.'),
      link: '#manual',
      linkText: translate('features.nearMe.link', 'Discover nearby reports'),
      color: featureColors[4],
    },
    {
      icon: '⚡',
      title: translate('features.automatic.title', 'Automatic Reporting'),
      description: translate('features.automatic.description', 'Configure automated weather reporting for selected locations on a schedule you define.'),
      link: '#manual',
      linkText: translate('features.automatic.link', 'Set up auto reports'),
      color: featureColors[5],
    },
  ];
  
  return (
    <FeaturesSection id="features" ref={featuresRef}>
      <SectionInner>
        <SectionHeader>
          <SectionTitle>{translate('features.title', 'Key Features')}</SectionTitle>
          <SectionSubtitle>
            {translate('features.subtitle', 'Discover the powerful capabilities that make WeatherTeller the leading community-driven weather reporting platform.')}
          </SectionSubtitle>
        </SectionHeader>
        
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard 
              key={index} 
              delay={`${0.1 * index}s`}
              className="feature-card"
            >
              <FeatureIconContainer backgroundColor={feature.color}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
              </FeatureIconContainer>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
              <FeatureLink href={feature.link}>{feature.linkText}</FeatureLink>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </SectionInner>
    </FeaturesSection>
  );
};

export default Features;